import { EventVisibilityType } from '@/graphql-types/globalTypes';

import { TICKET_MESSAGES } from './constants';

export const getAudienceLabel = (ticket: any): string => {
  if (ticket.canPurchase.type === 'MAGIC_LINK') {
    return 'Anyone with Ticket Link';
  }

  const { permissionConfig } = ticket.canPurchase;
  if (!permissionConfig) return 'General Admission';

  const { defaultPermission, permissionGroupIds, userIds } =
    permissionConfig.spec;

  if (defaultPermission === 'PUBLIC') return 'Anyone';
  if (defaultPermission === 'LOGGED_IN') return 'Any Logged-in User';
  if (defaultPermission === 'MEMBERS') return 'Members Only';
  if (defaultPermission === 'ADMINS') return 'Admins Only';
  if (permissionGroupIds && permissionGroupIds.length > 0)
    return 'Specific Groups';
  if (userIds && userIds.length > 0 && permissionConfig.allowList)
    return 'Specific People';

  return 'General Admission';
};

export const getAudienceBadgeColor = (audienceLabel: string): string => {
  switch (audienceLabel) {
    case 'Anyone':
    case 'Anyone with Ticket Link':
      return 'green';
    case 'Any Logged-in User':
      return 'blue';
    case 'Members Only':
      return 'purple';
    case 'Specific Groups':
      return 'orange';
    case 'Specific People':
      return 'red';
    case 'Admins Only':
      return 'pink';
    default:
      return 'gray';
  }
};

export const checkEventVisibility = (
  eventVisibilityType: string | undefined,
  eventGroupIds: string[],
  eventUserIds: string[],
  isAuthenticated: boolean,
  isMember: boolean,
  hasMembershipInCart: boolean,
  userGroupIds: string[],
  userId: string | undefined
): { meetsRequirement: boolean; reason?: string } => {
  if (!eventVisibilityType) {
    return { meetsRequirement: true };
  }

  switch (eventVisibilityType) {
    case EventVisibilityType.ANYONE:
      return { meetsRequirement: true };

    case EventVisibilityType.LOGGED_IN:
      if (!isAuthenticated) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.SIGN_IN_REQUIRED,
        };
      }
      return { meetsRequirement: true };

    case EventVisibilityType.MEMBERS:
      if (!isAuthenticated) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.SIGN_IN_REQUIRED,
        };
      }
      if (!isMember && !hasMembershipInCart) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.MEMBERS_ONLY,
        };
      }
      return { meetsRequirement: true };

    case EventVisibilityType.GROUP_MEMBERS:
      if (!isAuthenticated) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.SIGN_IN_REQUIRED,
        };
      }
      const isInEventGroup = eventGroupIds.some((groupId) =>
        userGroupIds.includes(groupId)
      );
      if (!isInEventGroup) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.GROUP_MEMBERS_ONLY,
        };
      }
      return { meetsRequirement: true };

    case EventVisibilityType.SPECIFIC_USERS:
      if (!isAuthenticated || !userId) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.SIGN_IN_REQUIRED,
        };
      }
      if (!eventUserIds.includes(userId)) {
        return {
          meetsRequirement: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.SPECIFIC_PEOPLE_ONLY,
        };
      }
      return { meetsRequirement: true };

    default:
      return { meetsRequirement: true };
  }
};

export const checkEligibility = (
  ticket: any,
  eventVisibilityType: string | undefined,
  eventGroupIds: string[],
  eventUserIds: string[],
  isAuthenticated: boolean,
  isMember: boolean,
  hasMembershipInCart: boolean,
  isAdmin: boolean,
  userGroupIds: string[],
  userId: string | undefined,
  isFromInvoice?: boolean,
  hasMembershipInInvoice?: boolean
): { isEligible: boolean; reason?: string } => {
  const eventVisibilityCheck = checkEventVisibility(
    eventVisibilityType,
    eventGroupIds,
    eventUserIds,
    isAuthenticated,
    isMember,
    hasMembershipInCart,
    userGroupIds,
    userId
  );

  if (isFromInvoice && !ticket.price.amount) {
    return { isEligible: false, reason: 'Invoice not needed for free ticket' };
  }

  if (!eventVisibilityCheck.meetsRequirement) {
    return { isEligible: false, reason: eventVisibilityCheck.reason };
  }

  if (ticket.canPurchase.type === 'MAGIC_LINK') {
    // Magic link tickets are always eligible, but authentication is still required for purchase
    return { isEligible: true };
  }

  const { permissionConfig } = ticket.canPurchase;
  if (!permissionConfig) return { isEligible: true };

  const { defaultPermission, permissionGroupIds, userIds } =
    permissionConfig.spec;

  if (!isAuthenticated) {
    return {
      isEligible: false,
      reason: TICKET_MESSAGES.ELIGIBILITY.SIGN_IN_REQUIRED,
    };
  }

  if (
    isFromInvoice &&
    defaultPermission === 'MEMBERS' &&
    hasMembershipInInvoice
  ) {
    return { isEligible: true };
  }

  if (eventVisibilityType === EventVisibilityType.GROUP_MEMBERS) {
    const isInEventGroup = eventGroupIds.some((groupId) =>
      userGroupIds.includes(groupId)
    );

    if (!isInEventGroup) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_RESTRICTED_GROUPS,
      };
    }

    if (defaultPermission === 'PUBLIC') {
      return { isEligible: true };
    }

    if (defaultPermission === 'MEMBERS') {
      if (!isMember) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_MEMBERS_GROUP,
        };
      }
      return { isEligible: true };
    }

    if (defaultPermission === 'ADMINS') {
      if (!isAdmin) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_ADMINS_GROUP,
        };
      }
      return { isEligible: true };
    }
  }

  if (eventVisibilityType === EventVisibilityType.LOGGED_IN) {
    if (defaultPermission === 'PUBLIC') {
      return { isEligible: true };
    }

    if (defaultPermission === 'MEMBERS') {
      if (!isMember) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_MEMBERS_LOGGED_IN,
        };
      }
      return { isEligible: true };
    }

    if (defaultPermission === 'ADMINS') {
      if (!isAdmin) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_ADMINS_LOGGED_IN,
        };
      }
      return { isEligible: true };
    }
  }

  if (eventVisibilityType === EventVisibilityType.MEMBERS) {
    if (!isMember && !hasMembershipInCart) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.MEMBERS_ONLY,
      };
    }

    if (defaultPermission === 'MEMBERS') {
      return { isEligible: true };
    }

    if (defaultPermission === 'ADMINS') {
      if (!isAdmin) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_ADMINS_MEMBERS,
        };
      }
      return { isEligible: true };
    }
  }

  if (eventVisibilityType === EventVisibilityType.SPECIFIC_USERS) {
    if (!userId || !eventUserIds.includes(userId)) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.SPECIFIC_PEOPLE_ONLY,
      };
    }

    if (defaultPermission === 'ADMINS') {
      if (!isAdmin) {
        return {
          isEligible: false,
          reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_ADMINS_SPECIFIC,
        };
      }
      return { isEligible: true };
    }
  }

  if (defaultPermission === 'PUBLIC') {
    return { isEligible: true };
  }

  if (defaultPermission === 'LOGGED_IN') {
    return { isEligible: true };
  }

  if (defaultPermission === 'MEMBERS') {
    if (!isMember && !hasMembershipInCart) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_MEMBERS_PRICING,
      };
    }
    return { isEligible: true };
  }

  if (defaultPermission === 'ADMINS') {
    if (!isAdmin) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_ADMINS_ONLY,
      };
    }
    return { isEligible: true };
  }

  if (permissionGroupIds && permissionGroupIds.length > 0) {
    const isInAnyGroup = permissionGroupIds.some((groupId: string) =>
      userGroupIds.includes(groupId)
    );
    if (!isInAnyGroup) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_RESTRICTED_GROUPS_GENERAL,
      };
    }
    return { isEligible: true };
  }

  if (userIds && userIds.length > 0 && permissionConfig.allowList) {
    if (!userId || !userIds.includes(userId)) {
      return {
        isEligible: false,
        reason: TICKET_MESSAGES.ELIGIBILITY.TICKET_SPECIFIC_PEOPLE,
      };
    }
    return { isEligible: true };
  }

  return { isEligible: true };
};

export const isUserRegisteredForTicket = (
  ticketId: string,
  userId: string | undefined,
  registrationsData: any
): boolean => {
  if (!userId) {
    return false;
  }

  const registrations =
    registrationsData?.event?.eventV2?.registrations?.results;
  if (registrations && Array.isArray(registrations)) {
    const isRegistered = registrations.some((reg: any) => {
      const attendeeMatches = reg.attendeeId === userId;
      const ticketMatches = reg.ticketId === ticketId;
      return attendeeMatches && ticketMatches;
    });
    if (isRegistered) {
      return true;
    }
  }

  return false;
};
